import { useRef, useState } from 'react'
import { formatarCnpj, normalizarCnpj, validarCnpj } from '@precad/compartilhado/validacao/cnpj'
import { normalizarEmail, validarEmail } from '@precad/compartilhado/validacao/email'
import { Campo } from '../componentes/Campo'
import { Turnstile, type TurnstileHandle } from '../componentes/Turnstile'
import { ACAO_INICIAR, TURNSTILE_SITEKEY } from '../config'

interface Props {
  aoIniciar: (cnpj: string, email: string, token: string) => Promise<boolean>
  ocupado: boolean
  aoAcompanhar: () => void
}

export function Identificacao({ aoIniciar, ocupado, aoAcompanhar }: Props) {
  const [cnpj, setCnpj] = useState('')
  const [email, setEmail] = useState('')
  const [token, setToken] = useState<string | null>(null)
  const [erros, setErros] = useState<{ cnpj?: string; email?: string; geral?: string }>({})

  const turnstile = useRef<TurnstileHandle | null>(null)

  const cnpjNorm = normalizarCnpj(cnpj)
  const emailNorm = normalizarEmail(email)

  function conferirCnpj() {
    if (!cnpjNorm) return setErros((e) => ({ ...e, cnpj: 'Informe o CNPJ da empresa.' }))
    if (!validarCnpj(cnpjNorm)) return setErros((e) => ({ ...e, cnpj: 'Este CNPJ não é válido. Confira os números.' }))
    setErros((e) => ({ ...e, cnpj: undefined }))
    setCnpj(formatarCnpj(cnpjNorm))
  }

  function conferirEmail() {
    if (!emailNorm) return setErros((e) => ({ ...e, email: 'Informe um e-mail.' }))
    if (!validarEmail(emailNorm)) return setErros((e) => ({ ...e, email: 'Este e-mail não parece válido.' }))
    setErros((e) => ({ ...e, email: undefined }))
  }

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault()
    if (ocupado) return

    const novos: typeof erros = {}
    if (!validarCnpj(cnpjNorm)) novos.cnpj = cnpjNorm ? 'Este CNPJ não é válido. Confira os números.' : 'Informe o CNPJ da empresa.'
    if (!validarEmail(emailNorm)) novos.email = emailNorm ? 'Este e-mail não parece válido.' : 'Informe um e-mail.'
    /* O botão nunca fica morto sem explicação: se falta a verificação, o
       clique DIZ o que falta em vez de não acontecer nada. */
    if (!token) novos.geral = 'Aguarde a verificação de segurança terminar, logo abaixo.'

    setErros(novos)
    if (Object.keys(novos).length) {
      document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
      return
    }

    const ok = await aoIniciar(cnpjNorm, emailNorm, token!)
    if (!ok) turnstile.current?.reiniciar()
  }

  return (
    <form onSubmit={enviar} noValidate>
      <div className="grade">
        <Campo
          rotulo="CNPJ"
          valor={cnpj}
          aoMudar={(v) => {
            setCnpj(v)
            if (erros.cnpj) setErros((e) => ({ ...e, cnpj: undefined }))
          }}
          aoSair={conferirCnpj}
          erro={erros.cnpj}
          destaque
          largo
          modo="text"
          maxLength={18}
          placeholder="00.000.000/0000-00"
          dica="Aceitamos o formato numérico e o novo formato com letras."
        />

        <Campo
          rotulo="E-mail da empresa"
          valor={email}
          aoMudar={(v) => {
            setEmail(v)
            if (erros.email) setErros((e) => ({ ...e, email: undefined }))
          }}
          aoSair={conferirEmail}
          erro={erros.email}
          largo
          tipo="email"
          modo="email"
          autoComplete="email"
          placeholder="contato@suaempresa.com.br"
          dica="Enviaremos para este endereço o código de acesso e o andamento da solicitação. Confira com atenção."
        />
      </div>

      <div className="turnstile">
        <Turnstile
          ref={turnstile}
          sitekey={TURNSTILE_SITEKEY}
          action={ACAO_INICIAR}
          aoObterToken={(t) => {
            setToken(t)
            if (t && erros.geral) setErros((e) => ({ ...e, geral: undefined }))
          }}
          aoFalhar={(m) => setErros((e) => ({ ...e, geral: m }))}
        />
      </div>

      {erros.geral ? (
        <p className="aviso aviso--erro" role="alert">
          {erros.geral}
        </p>
      ) : null}

      <div className="rodape">
        <span className="rodape__espaco" />
        <button type="submit" className="botao botao--principal botao--largo" aria-disabled={ocupado}>
          {ocupado ? 'Começando…' : 'Começar pré-cadastro'}
        </button>
      </div>

      {/* Fica DEPOIS do botão, e discreto, de propósito: quem chega aqui na
          maioria das vezes vem para se cadastrar. Oferecer as duas coisas com o
          mesmo peso faria a pessoa parar para escolher entre um caminho que ela
          quer e outro que não se aplica a ela. */}
      <p className="atalho">
        Já enviou sua solicitação?{' '}
        <button type="button" className="ligacao" onClick={aoAcompanhar}>
          Acompanhe o andamento
        </button>
      </p>
    </form>
  )
}
